import { useCallback } from 'react';
import type { Dispatch, SetStateAction } from 'react';
import type { ButtonPressControls } from './useButtonPressControls';

interface SharedButtonPressHandlersInput extends ButtonPressControls {
  isInteractive: boolean;
  disableHoverEffects: boolean;
}

interface UseButtonTouchHandlersInput extends SharedButtonPressHandlersInput {
  setIsTouchDevice: Dispatch<SetStateAction<boolean>>;
}

interface UseButtonMouseHandlersInput extends SharedButtonPressHandlersInput {
  isTouchDevice: boolean;
  setIsHovered: Dispatch<SetStateAction<boolean>>;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
}

export interface ButtonTouchHandlers {
  handleTouchStart: () => void;
  handleTouchEnd: () => void;
  handleTouchCancel: () => void;
}

export interface ButtonMouseHandlers {
  handleMouseEnter: () => void;
  handleMouseLeave: () => void;
  handleMouseDown: () => void;
  handleMouseUp: () => void;
}

export function useButtonTouchHandlers({
  isInteractive,
  disableHoverEffects,
  beginPress,
  releasePress,
  cancelPress,
  setIsTouchDevice,
}: UseButtonTouchHandlersInput): ButtonTouchHandlers {
  const handleTouchStart = useCallback((): void => {
    setIsTouchDevice(true);
    if (isInteractive && !disableHoverEffects) {
      beginPress();
    }
  }, [beginPress, disableHoverEffects, isInteractive, setIsTouchDevice]);

  const handleTouchEnd = useCallback((): void => {
    releasePress();
  }, [releasePress]);

  const handleTouchCancel = useCallback((): void => {
    cancelPress();
  }, [cancelPress]);

  return {
    handleTouchStart,
    handleTouchEnd,
    handleTouchCancel,
  };
}

export function useButtonMouseHandlers({
  isInteractive,
  disableHoverEffects,
  beginPress,
  releasePress,
  cancelPress,
  isTouchDevice,
  setIsHovered,
  onMouseEnter,
  onMouseLeave,
}: UseButtonMouseHandlersInput): ButtonMouseHandlers {
  const handleMouseEnter = useCallback((): void => {
    if (isInteractive && !isTouchDevice && !disableHoverEffects) {
      setIsHovered(true);
    }
    onMouseEnter?.();
  }, [disableHoverEffects, isInteractive, isTouchDevice, onMouseEnter, setIsHovered]);

  const handleMouseLeave = useCallback((): void => {
    cancelPress();
    onMouseLeave?.();
  }, [cancelPress, onMouseLeave]);

  const handleMouseDown = useCallback((): void => {
    if (isInteractive && !isTouchDevice && !disableHoverEffects) {
      beginPress();
    }
  }, [beginPress, disableHoverEffects, isInteractive, isTouchDevice]);

  const handleMouseUp = useCallback((): void => {
    if (!isTouchDevice) {
      releasePress();
    }
  }, [isTouchDevice, releasePress]);

  return {
    handleMouseEnter,
    handleMouseLeave,
    handleMouseDown,
    handleMouseUp,
  };
}
